"use client";

import React, { useEffect, useState } from "react";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Menu, User } from "lucide-react";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 animate-pulse rounded-md bg-muted/50" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative bg-transparent px-2 py-1 font-default text-base font-bold text-white transition-colors hover:bg-transparent hover:text-emerald-600 focus:bg-transparent data-[active=true]:bg-transparent after:absolute after:bottom-0 after:left-1/2 after:h-[1px] after:w-0 after:-translate-x-1/2 after:bg-emerald-600 after:transition-all after:duration-300 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-8 w-32 animate-pulse rounded-md bg-muted/50" />)}
      </div>;
  }
  return <div className="flex flex-col gap-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-default text-xl font-bold text-white transition-colors hover:text-emerald-600">
          {item.label}
        </Link>)}
    </div>;
}
export default function FraternalExcellenceHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="252277" id="header" className="sticky top-0 z-50 w-full transition-all duration-300 text-foreground border-b border-slate-200 light bg-primary">
      <div className="container mx-auto flex h-20 items-center justify-between px-4 bg-black">
        
        <div className="flex flex-1 items-center justify-start">
          <div className="lg:hidden">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:text-emerald-600">
                  <Menu className="size-6" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="mt-2 w-[90vw] max-w-[300px] p-6 rounded-sm border-border bg-popover">
                <MenuProvider menu_id="54">
                  <MobileMenuConsumer />
                </MenuProvider>
              </PopoverContent>
            </Popover>
          </div>
          <Link to="/" className="flex items-center">
            <WvcLogo className="h-12 w-auto" />
          </Link>
          <div className="hidden lg:block">
            <MenuProvider menu_id="54">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-end gap-2 md:gap-4">
          <div className="hidden w-48 md:block lg:w-64 opacity-0 pointer-events-none">
            <ProductsSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search indicators..." className="w-full" />
          </div>
          <Button variant="ghost" size="icon" className="text-foreground hover:text-emerald-600 rounded-sm">
            <User className="size-5" />
            <span className="sr-only">Bureau Login</span>
          </Button>
          <Link to="/ContactPage">
            <Button variant="outline" className="hidden md:flex border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-sm font-default text-xs uppercase tracking-wider">
              Media Enquiries
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}